// client/src/components/pages/RouteSummary/routeSummaryParams.ts (converted from .js, ST-00216)
//
// Small pure helpers for the Route Summary page (ST-00215).
import { toApiDateString } from '../../../utils/dateUtils';
import type { GroupingId, SummaryParamsSnapshot } from '../../../types/clientPages/routeSummary';

/** A selected route grouping: only the id and name are read here. */
interface SelectedGrouping {
  id: GroupingId;
  name: string;
}

/**
 * Default period: the 1st of this month to yesterday. On the 1st that range is
 * inverted (start today, end yesterday) and returned an empty table, so the
 * default becomes the whole previous month — the same rule Operations
 * Overview uses (RSO-20).
 */
export const defaultRouteSummaryPeriod = (
  today: Date = new Date()
): { startDate: string; endDate: string } => {
  if (today.getDate() === 1) {
    return {
      startDate: toApiDateString(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      endDate: toApiDateString(new Date(today.getFullYear(), today.getMonth(), 0)),
    };
  }
  return {
    startDate: toApiDateString(new Date(today.getFullYear(), today.getMonth(), 1)),
    endDate: toApiDateString(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1)),
  };
};

/**
 * Extra query params that make a drill-down (per-day list, Recon, Reg No list)
 * cover every route of a combined summary, not just the first (RSO-6). A single
 * route needs nothing extra: its id is in the URL.
 */
export const groupingScopeParams = (
  groupingIds: GroupingId[] | null | undefined
): { groupingIds?: string } =>
  Array.isArray(groupingIds) && groupingIds.length > 1
    ? { groupingIds: groupingIds.join(',') }
    : {};

/**
 * The parameters a summary was generated with. Drill-downs, Recon and the SQL
 * Query use these, not the live filter bar, so they always describe the table
 * on screen even after the dates, Min weight or route selection are changed
 * without pressing Generate again (RSO-10).
 */
export const snapshotSummaryParams = ({
  selectedGroupings,
  startDate,
  endDate,
  minWeight,
}: {
  selectedGroupings: SelectedGrouping[] | null | undefined;
  startDate: string;
  endDate: string;
  minWeight: number;
}): SummaryParamsSnapshot => ({
  groupingIds: (selectedGroupings || []).map((g) => g.id),
  groupingName:
    (selectedGroupings || []).length === 1
      ? selectedGroupings![0]!.name
      : `${(selectedGroupings || []).length} routes combined`,
  startDate,
  endDate,
  minWeight,
});
